/**
 * RecommendationPicker - the browse, curate and add surface over the vetted
 * Initial-Assessment recommendation library (35 topics, migration 0089).
 *
 * The Assessment Drafter opens it from the Recommendations section's insert
 * action, and the Assessment Letter shows it below the letter form. A care
 * manager:
 *   1. browses the grouped topic library and selects an entry,
 *   2. reads the RESOLVED paragraph (the client's first name and pronouns
 *      from the page's client block, already filled by the server - the model
 *      never rewrites the curated wording),
 *   3. for D9 entries curates the sub-list bullets (select / remove / add own),
 *      for D4 entries picks the relevant provider option(s),
 *   4. adds the assembled text to the report or the letter with one click, or
 *      copies it.
 *
 * Resolution is server-authoritative (feedback_server_authoritative_plan_derived):
 * the picker only displays what the endpoint produced and assembles it with
 * the care manager's curation (assembleRecommendationText). "Add to report"
 * hands over the text with its source: the entry (topic_slug +
 * entry_ordinal), the curation, and the pronoun choice the library was
 * resolved with, so the page can re-word it if the choice changes later.
 *
 * The library waits for a pronoun choice whenever the page says one is needed
 * (pronounsNeeded): a bound client with none chosen, or an incomplete custom
 * set. With no choice and none needed it resolves with they/them and says so.
 *
 * PR3 will prepend a ranked "Suggested topics" group above the browse groups in
 * the rail; the slot is marked below so it drops in without a rework.
 *
 * Design: matches the AI-Tools sage direction element-by-element. Cards =
 * paper + shadow-sh2 + rounded-card; eyebrow = sage-400 11.5px upper .16em;
 * headings = Playfair (font-display); the curate controls reuse the shared
 * modalFormStyles checkrow / chip primitives (#216); Copy is the primary
 * affordance (BTN_FOREST). Tokens only, no raw hexes, inline SVG (no lucide).
 */

import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  aiToolsApi,
  type RecommendationItem,
  type SpicePronounChoice,
} from '@/lib/api';
import { copyToClipboard } from '@/lib/clipboard';
import {
  assembleRecommendationText,
  parseCloser,
  recommendationsQueryKey,
  RECOMMENDATIONS_STALE_MS,
  type RecommendationCuration,
  type RecommendationSource,
} from '@/lib/recommendationText';
import {
  FLD,
  CHECKROW,
  CBOX,
  CBOX_OFF,
  CBOX_ON,
  CHIP_ADD,
  BTN_FOREST,
} from '@/components/primitives/modalFormStyles';
import { SearchField } from '@/components/primitives/SearchField';
import { TextInput } from '@/components/primitives/TextInput';
import { displayValue } from '@/lib/valueCase';
import { useSupportContact } from '@/hooks/useSupportContact';
import { contactSentence } from '@/lib/errorCopy';
import {
  GROUP_ORDER,
  FALLBACK_GROUP,
  groupKeyForSlug,
  type RecommendationGroup,
} from './recommendationGroups';

export interface RecommendationPickerProps {
  tenantSlug: string;
  /**
   * The resolution context (name + pronouns) is the page's client block;
   * the picker has no name or pronoun fields of its own. Empty name = the
   * library prompts for the client block instead of loading.
   */
  clientFirstName: string;
  /** Pronoun choice from the client block; undefined -> they/them, with a note. */
  pronouns?: SpicePronounChoice;
  /**
   * True while the page needs a pronoun choice before the library can load
   * (a bound client with none chosen, or an incomplete custom set): the
   * library is replaced by pronounPromptText and no request is made.
   */
  pronounsNeeded?: boolean;
  /** What the pronoun prompt says; the page names where the client block is. */
  pronounPromptText?: string;
  /**
   * When provided, the primary action is "Add to report": it hands the
   * assembled, server-resolved text to the page with its source (undefined
   * for an item that carries no entry_ordinal), and Copy drops to a secondary
   * affordance. When absent, the picker is a copy-out surface.
   *
   * Return `false` to signal the insert was DEFERRED (e.g. to a
   * replace-or-keep confirm); the "Added to report" flash is skipped.
   */
  onAddToReport?: (
    text: string,
    topic: string,
    needsResources: boolean,
    source: RecommendationSource | undefined,
  ) => boolean | void;
}

const DEFAULT_PRONOUN_PROMPT = "Choose the client's pronouns in the client block to load the library.";

const CARD_CLS = 'rounded-card bg-paper p-6 shadow-sh2 md:p-7';
const EYEBROW_CLS =
  'text-[11.5px] font-semibold uppercase tracking-[0.16em] text-sage-400';
// Secondary action pill (Copy, when "Add to report" is the primary).
const BTN_SECONDARY =
  'inline-flex items-center justify-center gap-2 h-[42px] px-5 rounded-full text-sm font-semibold border border-sage-800/[8%] bg-paper text-sage-800 shadow-sh1 transition-all duration-[var(--dur-base)] hover:shadow-sh2 disabled:opacity-50';

// ---- inline icons (no lucide) - currentColor so they inherit token text. ----
function CopyIcon(): ReactNode {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
function CheckIcon(): ReactNode {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 12.5l4.2 4.2L19 7"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function PlusIcon(): ReactNode {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}
function RemoveIcon(): ReactNode {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

/** A topic grouped under its display group, sorted for the rail. */
interface GroupedTopics {
  group: RecommendationGroup;
  items: RecommendationItem[];
}

function groupItems(items: RecommendationItem[]): GroupedTopics[] {
  const byKey = new Map<string, RecommendationItem[]>();
  for (const it of items) {
    const key = groupKeyForSlug(it.topic_slug);
    const bucket = byKey.get(key) ?? [];
    bucket.push(it);
    byKey.set(key, bucket);
  }
  const ordered: GroupedTopics[] = [];
  for (const group of [...GROUP_ORDER, FALLBACK_GROUP]) {
    const bucket = byKey.get(group.key);
    if (bucket && bucket.length > 0) {
      bucket.sort((a, b) => a.topic.localeCompare(b.topic));
      ordered.push({ group, items: bucket });
    }
  }
  return ordered;
}

export function RecommendationPicker({
  tenantSlug,
  clientFirstName,
  pronouns,
  pronounsNeeded = false,
  pronounPromptText = DEFAULT_PRONOUN_PROMPT,
  onAddToReport,
}: RecommendationPickerProps): ReactNode {
  const contact = useSupportContact();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Resolution context comes straight from the client block; the query
  // key carries the full pronoun choice so a pronoun change re-resolves.
  // The name is debounced so per-keystroke typing in the client block
  // doesn't fire a resolution query per letter.
  const [committedName, setCommittedName] = useState(clientFirstName.trim());
  useEffect(() => {
    const t = setTimeout(() => setCommittedName(clientFirstName.trim()), 300);
    return () => clearTimeout(t);
  }, [clientFirstName]);
  const pronounChoice: SpicePronounChoice = pronouns ?? { preset: 'they_them' };

  const query = useQuery({
    queryKey: recommendationsQueryKey(tenantSlug, committedName, pronounChoice),
    queryFn: () =>
      aiToolsApi.listRecommendations(tenantSlug, {
        client_first_name: committedName,
        pronouns: pronounChoice,
      }),
    enabled: committedName.length > 0 && !pronounsNeeded,
    staleTime: RECOMMENDATIONS_STALE_MS,
  });

  const items = query.data?.items ?? [];
  const selected = items.find((it) => it.id === selectedId) ?? null;

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q.length === 0 ? items : items.filter((it) => it.topic.toLowerCase().includes(q));
    return groupItems(filtered);
  }, [items, search]);

  return (
    <section className={CARD_CLS} data-testid="recommendation-picker">
      <span className={EYEBROW_CLS}>Recommendations library</span>
      <h2 className="mt-2 font-display text-[22px] font-semibold tracking-[-0.01em] text-sage-800">
        Browse vetted recommendations
      </h2>

      {/* Browse rail + preview. */}
      {committedName.length === 0 ? (
        <div
          className="mt-6 rounded-xl border border-dashed border-sage-300 bg-paper px-5 py-10 text-center text-sm text-sage-400"
          data-testid="recommendation-empty-prompt"
        >
          Add the client’s first name in the client block to load the library.
        </div>
      ) : pronounsNeeded ? (
        <div
          className="mt-6 rounded-xl border border-dashed border-sage-300 bg-paper px-5 py-10 text-center text-sm text-sage-400"
          data-testid="recommendation-pronoun-prompt"
        >
          {pronounPromptText}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          className="mt-6 rounded-xl border border-warm-200 bg-warm-50 px-4 py-3 text-sm text-warm-700"
          data-testid="recommendation-error"
        >
          {contactSentence(
            "The recommendation library didn't load. Reload the page, and if it happens again, tell {contact_system}.",
            contact,
          )}
        </div>
      ) : (
        <>
          {pronouns === undefined && (
            <p className="mt-6 text-xs text-ink-soft" data-testid="recommendation-pronoun-default-note">
              {"These use 'they' and 'them' until you choose pronouns."}
            </p>
          )}
          <div
            className={
              (pronouns === undefined ? 'mt-3' : 'mt-6') +
              ' grid grid-cols-1 items-start gap-6 md:grid-cols-[minmax(0,320px)_minmax(0,1fr)]'
            }
          >
            {/* Rail */}
            <div data-testid="recommendation-rail">
              <SearchField
                value={search}
                onChange={setSearch}
                placeholder="Search"
                aria-label="Search recommendation topics"
                testId="recommendation-search"
              />

              <div className="mt-3 max-h-[560px] overflow-y-auto pr-1">
                {/* PR3 slot: the ranked "Suggested topics" group mounts here,
                    above the clinical browse groups, without reworking the rail. */}

                {query.isLoading ? (
                  <p className="px-1 py-6 text-sm text-sage-400" data-testid="recommendation-loading">
                    Loading the library…
                  </p>
                ) : grouped.length === 0 ? (
                  <p className="px-1 py-6 text-sm text-sage-400">No topics match “{search.trim()}”.</p>
                ) : (
                  grouped.map(({ group, items: groupItemsList }) => (
                    <div key={group.key} className="mb-4 last:mb-0">
                      <p className={`${EYEBROW_CLS} mb-1.5 px-1`}>{group.label}</p>
                      <ul className="space-y-0.5">
                        {groupItemsList.map((it) => {
                          const active = it.id === selectedId;
                          return (
                            <li key={it.id}>
                              <button
                                type="button"
                                onClick={() => setSelectedId(it.id)}
                                aria-current={active}
                                className={
                                  'w-full rounded-[10px] px-3 py-2 text-left text-[13.5px] leading-snug transition-colors duration-[var(--dur-base)] ' +
                                  (active
                                    ? 'bg-tick-affirm font-semibold text-sage-800'
                                    : 'text-ink hover:bg-sand')
                                }
                                data-testid={`recommendation-topic-${it.topic_slug}`}
                              >
                                {it.topic}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Preview */}
            <div data-testid="recommendation-preview">
              {selected && (
                <RecommendationDetail
                  key={selected.id}
                  item={selected}
                  wordedWith={pronounChoice}
                  onAddToReport={onAddToReport}
                />
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

const CLOSER_TAG: Record<RecommendationItem['closer_type'], string | null> = {
  none: null,
  baked_in: 'Includes vetted providers',
  directory_generic: 'You add the specifics',
};

/**
 * RecommendationDetail - the focal resolved preview + curate controls + Copy.
 * Keyed by item id so its selection state resets when the topic changes.
 */
function RecommendationDetail({
  item,
  wordedWith,
  onAddToReport,
}: {
  item: RecommendationItem;
  /** The pronoun choice in the query key that produced this item. */
  wordedWith: SpicePronounChoice;
  onAddToReport?: RecommendationPickerProps['onAddToReport'];
}): ReactNode {
  const closer = item.closer_text ? parseCloser(item.closer_text) : null;
  const isMultiOption = !!closer && closer.options.length >= 2;

  // D9 sub-list: seeded bullets (selectable) + the CM's own additions.
  const [subSelected, setSubSelected] = useState<boolean[]>(() => item.sub_items.map(() => true));
  const [extraItems, setExtraItems] = useState<string[]>([]);
  const [draftItem, setDraftItem] = useState('');

  // D4 multi-option closer: which provider options to include.
  const [optSelected, setOptSelected] = useState<boolean[]>(() =>
    isMultiOption ? closer!.options.map(() => true) : [],
  );
  // Single (non-multi) closer: include it or not.
  const [includeCloser, setIncludeCloser] = useState(true);

  const [copied, setCopied] = useState(false);
  const [added, setAdded] = useState(false);

  const tag = CLOSER_TAG[item.closer_type];

  const curation = (): RecommendationCuration => ({
    sub_selected: [...subSelected],
    extra_items: [...extraItems],
    options_selected: [...optSelected],
    include_closer: includeCloser,
  });

  const buildCopyText = (): string => assembleRecommendationText(item, curation());

  const handleCopy = async (): Promise<void> => {
    try {
      await copyToClipboard(buildCopyText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const addExtra = (): void => {
    const v = draftItem.trim();
    if (v.length === 0) return;
    setExtraItems((prev) => [...prev, v]);
    setDraftItem('');
  };

  const handleAddToReport = (): void => {
    const chosen = curation();
    // The source lets the page re-word this text if the pronoun choice
    // changes; an item without an entry number cannot be found again.
    const source: RecommendationSource | undefined =
      item.entry_ordinal !== undefined && Number.isInteger(item.entry_ordinal) && item.entry_ordinal >= 1
        ? {
            ref: { topic_slug: item.topic_slug, entry_ordinal: item.entry_ordinal },
            curation: chosen,
            worded_with: wordedWith,
          }
        : undefined;
    // directory_generic closers need the CM to drop in specific providers ->
    // drive the [ADD RESOURCES HERE] marker off the tag.
    const result = onAddToReport?.(
      assembleRecommendationText(item, chosen),
      item.topic,
      item.closer_type === 'directory_generic',
      source,
    );
    // false = the insert was deferred (replace-or-keep confirm); don't
    // flash "Added to report" for something that hasn't landed.
    if (result === false) return;
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h3 className="font-display text-[20px] font-semibold leading-tight tracking-[-0.01em] text-sage-800">
          {item.topic}
        </h3>
        {tag && (
          <span className="shrink-0 rounded-pill bg-sand px-3 py-1 text-[11.5px] font-semibold text-sage-400">
            {tag}
          </span>
        )}
      </div>

      {/* Focal resolved paragraph - the deliverable, ready to paste. */}
      <div
        className="whitespace-pre-wrap rounded-xl border border-sage-400/15 bg-sand/50 p-5 text-[14.5px] leading-relaxed text-ink"
        data-testid="recommendation-body"
      >
        {item.body}
      </div>

      {/* D9 - sub-list bullets: select / remove / add your own. */}
      {(item.sub_items.length > 0 || extraItems.length > 0) && (
        <div data-testid="recommendation-sublist">
          <p className={`${EYEBROW_CLS} mb-2`}>Choose what to include</p>
          <div className="space-y-0.5">
            {item.sub_items.map((sub, i) => (
              <label key={`seed-${i}`} className={CHECKROW}>
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={subSelected[i]}
                  onChange={() =>
                    setSubSelected((prev) => prev.map((v, j) => (j === i ? !v : v)))
                  }
                />
                <span className={`${CBOX} ${subSelected[i] ? CBOX_ON : CBOX_OFF}`}>
                  {subSelected[i] && <CheckIcon />}
                </span>
                <span className="text-[13.5px] leading-snug text-ink">{sub}</span>
              </label>
            ))}
            {extraItems.map((extra, i) => (
              <div key={`extra-${i}`} className={`${CHECKROW} group/extra`}>
                <span className={`${CBOX} ${CBOX_ON}`}>
                  <CheckIcon />
                </span>
                <span className="flex-1 text-[13.5px] leading-snug text-ink">{displayValue(extra)}</span>
                <button
                  type="button"
                  onClick={() => setExtraItems((prev) => prev.filter((_, j) => j !== i))}
                  aria-label={`Remove ${extra}`}
                  className="shrink-0 rounded-full p-1 text-sage-300 transition-colors hover:text-sage-800"
                >
                  <RemoveIcon />
                </button>
              </div>
            ))}
          </div>

          {/* Add your own bullet. */}
          <div className="mt-2.5 flex items-center gap-2">
            <TextInput
              type="text"
              value={draftItem}
              onChange={(e) => setDraftItem(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addExtra();
                }
              }}
              placeholder="Add your own…"
              aria-label="Add your own item"
              className={`${FLD} h-[38px] flex-1`}
              data-testid="recommendation-add-input"
            />
            <button
              type="button"
              onClick={addExtra}
              disabled={draftItem.trim().length === 0}
              className={`${CHIP_ADD} h-[38px] disabled:opacity-50`}
              data-testid="recommendation-add-btn"
            >
              <PlusIcon />
              Add
            </button>
          </div>
        </div>
      )}

      {/* Closer. D4 = multi-option provider select; otherwise a single toggle. */}
      {closer && isMultiOption && (
        <div data-testid="recommendation-options">
          <p className={`${EYEBROW_CLS} mb-2`}>Choose the relevant option(s)</p>
          {closer.lead && <p className="mb-2 text-[13.5px] leading-snug text-ink-soft">{closer.lead}</p>}
          <div className="space-y-0.5">
            {closer.options.map((opt, i) => (
              <label key={i} className={CHECKROW}>
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={optSelected[i]}
                  onChange={() =>
                    setOptSelected((prev) => prev.map((v, j) => (j === i ? !v : v)))
                  }
                />
                <span className={`${CBOX} ${optSelected[i] ? CBOX_ON : CBOX_OFF}`}>
                  {optSelected[i] && <CheckIcon />}
                </span>
                <span className="text-[13.5px] leading-snug text-ink">{opt}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {closer && !isMultiOption && (
        <div data-testid="recommendation-closer">
          <p className={`${EYEBROW_CLS} mb-2`}>Closing line</p>
          <label className={CHECKROW}>
            <input
              type="checkbox"
              className="sr-only"
              checked={includeCloser}
              onChange={() => setIncludeCloser((v) => !v)}
            />
            <span className={`${CBOX} ${includeCloser ? CBOX_ON : CBOX_OFF}`}>
              {includeCloser && <CheckIcon />}
            </span>
            <span className="text-[13.5px] leading-snug text-ink">{item.closer_text}</span>
          </label>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 pt-1">
        {onAddToReport && (
          <button
            type="button"
            onClick={handleAddToReport}
            className={BTN_FOREST}
            data-testid="recommendation-add-to-report-btn"
          >
            {added ? <CheckIcon /> : <PlusIcon />}
            {added ? 'Added to report' : 'Add to report'}
          </button>
        )}
        <button
          type="button"
          onClick={handleCopy}
          className={onAddToReport ? BTN_SECONDARY : BTN_FOREST}
          data-testid="recommendation-copy-btn"
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
          {copied ? 'Copied' : onAddToReport ? 'Copy' : 'Copy for the report'}
        </button>
      </div>
    </div>
  );
}
